import { ChevronDown, ChevronRight, Calculator, Upload, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CLIENT_TYPES, JOB_STATUSES, STATUS_COLORS } from "./constants";

export function JobDetailsSection({
  job,
  setJob,
  isExpanded,
  onToggle,
  isAdmin,
  setHasUnsavedChanges,
  handleClientTypeChange,
}) {
  const updateJob = (updates) => {
    setHasUnsavedChanges(true);
    setJob({ ...job, ...updates });
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <Card data-testid="job-details-card">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-4 cursor-pointer hover:bg-slate-50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="section-header text-lg flex items-center gap-2">
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Calculator className="w-5 h-5" />
                Job Details
                {job.job_name && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    – {job.job_name}
                  </span>
                )}
              </CardTitle>
              <Badge 
                variant="outline" 
                className={`${STATUS_COLORS[job.status] || STATUS_COLORS.Draft}`}
                data-testid="job-status-badge"
              >
                {job.status}
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Job Name, Quote Date, Status */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job-name">Job Name</Label>
                <Input
                  id="job-name"
                  placeholder="e.g., ABC Corp Staff Uniforms"
                  value={job.job_name}
                  onChange={(e) => updateJob({ job_name: e.target.value })}
                  data-testid="job-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-date">Quote Date</Label>
                <Input
                  id="quote-date"
                  type="date"
                  value={job.quote_date}
                  onChange={(e) => updateJob({ quote_date: e.target.value })}
                  data-testid="quote-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job-status">Status</Label>
                <Select
                  value={job.status}
                  onValueChange={(v) => updateJob({ status: v })}
                >
                  <SelectTrigger id="job-status" data-testid="job-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-type">Client Type</Label>
                <Select
                  value={job.client_type}
                  onValueChange={handleClientTypeChange}
                >
                  <SelectTrigger id="client-type" data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(CLIENT_TYPES).map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="margin">Margin %</Label>
                  <Input
                    id="margin"
                    type="number"
                    step="0.01"
                    value={(job.margin_percent * 100).toFixed(0)}
                    onChange={(e) =>
                      updateJob({ margin_percent: parseFloat(e.target.value) / 100 || 0 })
                    }
                    data-testid="margin-input"
                    className="font-mono"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="shipping">Shipping</Label>
                <Input
                  id="shipping"
                  type="number"
                  step="0.01"
                  value={job.shipping}
                  onChange={(e) =>
                    updateJob({ shipping: parseFloat(e.target.value) || 0 })
                  }
                  data-testid="shipping-input"
                  className="font-mono"
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="misc">Misc Costs</Label>
                  <Input
                    id="misc"
                    type="number"
                    step="0.01"
                    value={job.misc_costs}
                    onChange={(e) =>
                      updateJob({ misc_costs: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="misc-costs-input"
                    className="font-mono"
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Design Fee Section */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4 text-slate-600" />
                  <Label className="text-sm font-medium">Design Required</Label>
                </div>
                <Switch
                  checked={job.design_required}
                  onCheckedChange={(checked) => updateJob({ design_required: checked })}
                  data-testid="design-required-switch"
                />
              </div>
              
              {job.design_required && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="design-fee" className="flex items-center gap-1.5 text-sm">
                      <DollarSign className="w-3.5 h-3.5 text-slate-500" />
                      Design Fee
                    </Label>
                    <Input
                      id="design-fee"
                      type="number"
                      step="0.01"
                      min="0"
                      value={job.design_fee}
                      onChange={(e) =>
                        updateJob({ design_fee: parseFloat(e.target.value) || 0 })
                      }
                      className="font-mono"
                      data-testid="design-fee-input"
                    />
                  </div>
                  {isAdmin && (
                    <div className="flex items-end">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="apply-margin-design"
                          checked={job.apply_margin_to_design}
                          onCheckedChange={(checked) =>
                            updateJob({ apply_margin_to_design: checked })
                          }
                          data-testid="apply-margin-design-switch"
                        />
                        <Label htmlFor="apply-margin-design" className="text-sm text-slate-600">
                          Apply margin to design
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default JobDetailsSection;
